export const HONGGUO_ANALYSIS_POLL_MS = 2_000;
export const HONGGUO_ANALYSIS_MAX_WAIT_MS = 5 * 60_000;
const JOB_STATUSES = new Set(['RUNNING', 'SUCCEEDED', 'FAILED']);
const text = (value, limit = 20_000) => typeof value === 'string' ? value.trim().slice(0, limit) : '';
const textList = value => Array.isArray(value) ? value.map(item => text(item)).filter(Boolean).slice(0, 30) : [];

export const hongguoAnalysisKey = ({ board, page, seriesId }) => JSON.stringify([board, page, seriesId]);

export const createHongguoAnalysisRequestId = () => {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  if (!globalThis.crypto?.getRandomValues) throw new Error('当前浏览器无法创建分析请求，请使用安全连接后重试。');
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = [...bytes].map(value => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

// Sources are ordinary public HTTP(S) links, never executable markup or local destinations.
export const safeAnalysisSourceUrl = value => {
  if (typeof value !== 'string' || value.length > 4_096 || /[\s\\]/.test(value) || /%(?:0[\da-f]|1[\da-f]|7f)/i.test(value)
    || [...value].some(char => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127)) return null;
  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase();
    const labels = host.split('.');
    if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password || url.port || labels.length < 2
      || labels.some(label => !/^[a-z\d](?:[a-z\d-]{0,61}[a-z\d])?$/.test(label))
      || !/^(?:[a-z]{2,63}|xn--[a-z\d-]+)$/.test(labels.at(-1))
      || /(?:^|\.)(?:localhost|local|lan|internal|intranet|home|test|invalid|example|onion|arpa)$/.test(host)) return null;
    return url.href;
  } catch { return null; }
};

export const normalizeHongguoAnalysisJob = (data, expected) => {
  if (!data || data.requestId !== expected.requestId || data.seriesId !== expected.seriesId || !JOB_STATUSES.has(data.status)) {
    throw new Error('分析响应与当前剧集不匹配，已停止查询。');
  }
  const input = data.result;
  let result = null;
  if (input && typeof input === 'object' && !Array.isArray(input)) {
    const seen = new Set();
    const sources = (Array.isArray(input.sources) ? input.sources : []).flatMap(item => {
      const url = safeAnalysisSourceUrl(item?.url);
      if (!url || seen.has(url)) return [];
      seen.add(url);
      return [{ title: text(item.title, 500) || new URL(url).hostname, url }];
    }).slice(0, 30);
    result = {
      summary: text(input.summary),
      sections: (Array.isArray(input.sections) ? input.sections : []).filter(item => item && typeof item === 'object')
        .map(item => ({ title: text(item.title, 500), content: text(item.content, 50_000) })).filter(item => item.content).slice(0, 20),
      directions: textList(input.directions),
      unknowns: textList(input.unknowns),
      sources,
      searchStatus: input.searchStatus === 'VERIFIED' && sources.length ? 'VERIFIED' : 'UNVERIFIED',
      generatedAt: text(input.generatedAt, 100), sourceFetchedAt: text(input.sourceFetchedAt, 100),
      warning: text(input.warning, 2_000), rawText: text(input.rawText, 100_000),
    };
  }
  if (data.status === 'SUCCEEDED' && !result) throw new Error('分析已结束，但结果暂不可读，请查询已有结果。');
  return {
    requestId: data.requestId, seriesId: data.seriesId, title: text(data.title, 500), status: data.status,
    createdAt: text(data.createdAt, 100), completedAt: text(data.completedAt, 100),
    error: text(data.error, 2_000), result,
  };
};

/** One paid POST per explicit intent. Closing pauses reads, not the server job. */
export const createHongguoAnalysisController = ({
  startJob, fetchJob, now = Date.now, setTimer = setTimeout, clearTimer = clearTimeout,
  createRequestId = createHongguoAnalysisRequestId,
}) => {
  const entries = new Map();
  const listeners = new Set();
  let activeKey = null;
  let timer = null;
  let query = null;
  let snapshot = { activeKey: null, entry: null };

  const publish = () => {
    const entry = entries.get(activeKey) || null;
    if (snapshot.activeKey === activeKey && snapshot.entry === entry) return;
    snapshot = { activeKey, entry };
    listeners.forEach(listener => listener());
  };
  const current = entry => entries.get(entry.key)?.requestId === entry.requestId;
  const patch = (entry, update) => {
    if (!current(entry)) return null;
    const next = { ...entries.get(entry.key), ...update };
    entries.set(entry.key, next);
    publish();
    return next;
  };
  const pause = () => {
    if (timer != null) clearTimer(timer);
    timer = null;
    if (query) {
      query.controller.abort();
      if (query.deadline != null) clearTimer(query.deadline);
      const entry = entries.get(query.key);
      if (entry?.requestId === query.requestId) patch(entry, { querying: false });
    }
    query = null;
  };
  const expire = entry => patch(entry, {
    phase: 'timeout', querying: false, error: '已暂停等待，分析可能仍在进行。可查询已有结果，或稍后再查看。',
  });
  const receive = (entry, data) => {
    const job = normalizeHongguoAnalysisJob(data, { requestId: entry.requestId, seriesId: entry.selection.seriesId });
    return patch(entry, {
      job, querying: false, phase: job.status === 'SUCCEEDED' ? 'success' : job.status === 'FAILED' ? 'failed' : 'running',
      error: job.status === 'FAILED' ? job.error || '本次分析失败。可以重新分析。' : '',
    });
  };
  const schedule = entry => {
    if (!entry || activeKey !== entry.key || !current(entry) || entry.phase !== 'running') return;
    const remaining = entry.deadlineAt - now();
    if (remaining <= 0) { expire(entry); return; }
    timer = setTimer(() => {
      timer = null;
      read(entry);
    }, Math.min(HONGGUO_ANALYSIS_POLL_MS, remaining));
  };
  const read = async (entry, explicit = false) => {
    if (!current(entry) || activeKey !== entry.key || entry.phase === 'submitting' || query) return;
    const remaining = entry.deadlineAt - now();
    if (remaining <= 0 && !explicit) { expire(entry); return; }
    pause();
    const request = { controller: new AbortController(), key: entry.key, requestId: entry.requestId };
    query = request;
    // Also bound a slow/hung GET. A manual query after expiry is one read, never a new polling window.
    const deadline = setTimer(() => {
      if (query !== request) return;
      request.controller.abort();
      query = null;
      expire(entry);
    }, remaining > 0 ? remaining : 15_000);
    request.deadline = deadline;
    patch(entry, { querying: true, error: '' });
    try {
      const data = await fetchJob(entry.requestId, { signal: request.controller.signal });
      if (request.controller.signal.aborted || query !== request || !current(entry)) return;
      const next = receive(entry, data);
      query = null;
      schedule(next);
    } catch (cause) {
      if (!request.controller.signal.aborted && query === request && current(entry)) {
        query = null;
        patch(entry, { phase: 'query-error', querying: false, error: text(cause?.message, 2_000) || '暂时无法读取分析结果。请查询已有结果，不会重复提交。' });
      }
    } finally {
      clearTimer(deadline);
      if (query === request) {
        query = null;
        patch(entry, { querying: false });
      }
    }
  };
  const begin = selection => {
    const key = hongguoAnalysisKey(selection);
    let requestId;
    try { requestId = createRequestId(); } catch (cause) {
      const entry = { key, selection, requestId: '', phase: 'failed', error: cause.message };
      entries.set(key, entry);
      publish();
      return;
    }
    const startedAt = now();
    const entry = { key, selection, requestId, phase: 'submitting', job: null, error: '', startedAt,
      deadlineAt: startedAt + HONGGUO_ANALYSIS_MAX_WAIT_MS, querying: false };
    entries.set(key, entry);
    publish();
    // Do not abort/replay a paid POST on modal close. Its late result stays in its own row cache.
    Promise.resolve().then(() => startJob({
      board: selection.board, page: selection.page, seriesId: selection.seriesId, requestId,
    })).then(data => {
      if (!current(entry)) return;
      schedule(receive(entry, data));
    }).catch(cause => {
      if (!current(entry)) return;
      patch(entry, { phase: 'unknown', error: text(cause?.message, 2_000)
        || '提交结果暂未确认。请先查询已有任务；系统不会自动重复提交。' });
    });
  };

  return {
    getSnapshot: () => snapshot,
    subscribe: listener => { listeners.add(listener); return () => listeners.delete(listener); },
    open: selection => {
      if (!selection || !/^\d{1,30}$/.test(selection.seriesId || '')) return;
      pause();
      activeKey = hongguoAnalysisKey(selection);
      const entry = entries.get(activeKey);
      if (!entry) begin({ board: selection.board, page: selection.page, seriesId: selection.seriesId, title: selection.title });
      else {
        publish();
        if (entry.requestId && !['submitting', 'success', 'failed'].includes(entry.phase)) read(entry, true);
      }
    },
    close: () => { pause(); activeKey = null; publish(); },
    check: () => {
      const entry = entries.get(activeKey);
      if (entry?.requestId && entry.phase !== 'submitting') read(entry, true);
    },
    reanalyze: () => {
      const entry = entries.get(activeKey);
      if (!entry || ['submitting', 'running'].includes(entry.phase) || entry.querying) return;
      pause();
      begin(entry.selection);
    },
    dispose: () => { pause(); activeKey = null; publish(); listeners.clear(); },
  };
};
